import { SpsAbstractControl, SpsAbstractControlConfig } from "./SpsAbstractControl.interface";
import { SpsControlSet, getMember } from "./SpsControlSet.interface";

import { SpsValidators } from "../SpsValidators";

export interface SpsFormArray<T> extends SpsControlSet {
    members: Array<SpsAbstractControl<T>>;
    length: number;
    at(index: number): SpsAbstractControl<T>;
    push(control: SpsAbstractControl<T>);
    insert(index: number, control: SpsAbstractControl<T>);
    removeAt(index: number);
    setControl(index: number, control: SpsAbstractControl<T>);
}

export function formArray<T>(members: Array<SpsAbstractControl<T>> = [], config: SpsAbstractControlConfig<T> = {}): SpsFormArray<T> {
    return <SpsFormArray<T>>{
        validators: [],
        ...config,
        _type: "Array",
        members: [...members],
        errors: null,
        invalid: false,
        isValid() {
            return !this.invalid && this.members.every(member => member.isValid());
        },
        isRequired() {
            return this.validators && this.validators.indexOf(SpsValidators.required) > -1;
        },
        hasError(errorKey: string) {
            return this.errors && this.errors.hasOwnProperty(errorKey);
        },
        length: members.length,
        setInitValue(newValue: T[]): SpsFormArray<T> {
            for (const [i, member] of this.members.entries()) {
                member.setInitValue(newValue[i]);
            }
            return this;
        },
        getValue(): T[] {
            return this.members.map((member: SpsAbstractControl<T>) => member.getValue());
        },
        setValue(newValue: T[] = []): SpsFormArray<T> {
            for (const [i, member] of this.members.entries()) {
                member.setValue(newValue[i]);
            }
            return this;
        },
        update(): SpsFormArray<T> {
            return this;
        },
        isFocused() {
            return this.members.some(member => member.isFocused());
        },
        focus(): SpsFormArray<T> {
            if (this.members.length) {
                this.members[0].focus();
            }
            return this;
        },
        blur(): SpsFormArray<T> {
            for (const member of this.members) {
                member.blur();
            }
            return this;
        },
        isPristine() {
            return this.members.every(member => member.isPristine());
        },
        markAsPristine(): SpsFormArray<T> {
            for (const member of this.members) {
                member.markAsPristine();
            }
            return this;
        },
        markAsDirty(): SpsFormArray<T> {
            for (const member of this.members) {
                member.markAsDirty();
            }
            return this;
        },
        reset(value?: T[]): SpsFormArray<T> {
            this.members = [...members];
            this.length = members.length;
            for (const [i, member] of this.members.entries()) {
                if (value && i < value.length) {
                    member.reset(value[i]);
                } else {
                    member.reset();
                }
            }
            this.update();
            return this;
        },
        get<K extends SpsAbstractControl<any>>(path: PropertyKey | PropertyKey[]): K {
            return getMember<K>(this, path);
        },
        at(index: number) {
            return this.members[index];
        },
        push(control: SpsAbstractControl<T>): SpsFormArray<T> {
            this.members.push(control);
            this.length = this.members.length;
            this.update();
            return this;
        },
        insert(index: number, control: SpsAbstractControl<T>): SpsFormArray<T> {
            this.members.splice(index, null, control);
            this.length = this.members.length;
            this.update();
            return this;
        },
        removeAt(index: number): SpsFormArray<T> {
            this.members.splice(index, 1);
            this.length = this.members.length;
            this.update();
            return this;
        },
        setControl(index: number, control: SpsAbstractControl<T>): SpsFormArray<T> {
            this.members[index] = control;
            this.update();
            return this;
        },
    };
}
